import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

export default function GoogleReviews() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/reviews")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data || !data.reviews || data.reviews.length === 0) return null;

  return (
    <section
      data-testid="google-reviews"
      className="relative py-28 md:py-40 bg-[#0a0a0a] border-t border-white/5 overflow-hidden"
    >
      {/* Subtle radial glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          background:
            "radial-gradient(ellipse at 20% 0%, rgba(225,6,0,0.12) 0%, transparent 55%)",
        }}
      />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-12">
        {/* Header — two column, editorial */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-end mb-16 md:mb-20">
          <div className="md:col-span-8">
            <p className="eyebrow mb-6">Voices — 06 / 07</p>
            <h2 className="font-serif text-5xl md:text-7xl font-light text-white leading-[0.95]">
              What our clients{" "}
              <span className="italic text-neutral-400">say.</span>
            </h2>
            <p className="mt-6 text-neutral-400 max-w-md text-base md:text-lg leading-relaxed">
              Live from Google — the actual reviews people write after seeing
              their vehicle come back from the SEVEN7H studio.
            </p>
          </div>

          <div className="md:col-span-4 md:text-right">
            <div className="inline-flex md:flex md:justify-end items-baseline gap-3">
              <p
                data-testid="reviews-rating"
                className="font-serif text-7xl md:text-8xl text-white leading-none"
              >
                {data.rating?.toFixed(1) ?? "5.0"}
              </p>
              <div className="flex gap-0.5 pb-3">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={18}
                    className="fill-brand-red text-brand-red"
                    strokeWidth={0}
                  />
                ))}
              </div>
            </div>
            <p className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">
              Based on {data.total ?? data.reviews.length} Google reviews
            </p>
          </div>
        </div>

        {/* Review wall — asymmetric offset grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {data.reviews.map((r, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`relative bg-white/[0.02] border border-white/10 p-7 md:p-9 hover:border-white/25 transition-colors duration-500 ${
                // Asymmetric spans + offsets for a magazine-y feel
                i === 0
                  ? "md:col-span-5 md:row-span-2"
                  : i === 1
                  ? "md:col-span-4 md:col-start-7"
                  : i === 2
                  ? "md:col-span-3 md:col-start-11 md:row-span-2"
                  : i === 3
                  ? "md:col-span-3 md:col-start-6 md:mt-4"
                  : "md:col-span-4 md:col-start-9 md:mt-4 md:hidden lg:block"
              }`}
              data-testid={`review-card-${i}`}
            >
              {/* Rating */}
              <div className="flex gap-0.5 mb-5">
                {[...Array(5)].map((_, s) => (
                  <Star
                    key={s}
                    size={12}
                    strokeWidth={0}
                    className={
                      s < (r.rating ?? 0)
                        ? "fill-brand-red text-brand-red"
                        : "fill-neutral-800 text-neutral-800"
                    }
                  />
                ))}
              </div>

              {/* Body */}
              <p
                className={`text-white/85 leading-relaxed font-serif ${
                  i === 0 ? "text-xl md:text-2xl" : "text-base md:text-lg"
                }`}
              >
                “{r.text}”
              </p>

              {/* Attribution */}
              <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-3">
                {r.avatar ? (
                  <img
                    src={r.avatar}
                    alt={r.author}
                    referrerPolicy="no-referrer"
                    className="w-8 h-8 rounded-full border border-white/10"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full border border-white/10 bg-white/5 flex items-center justify-center text-white text-xs font-mono">
                    {(r.author || "?").charAt(0)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm truncate">{r.author}</p>
                  <p className="font-mono text-[10px] tracking-widest uppercase text-neutral-500">
                    {r.when}
                  </p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Read all on Google CTA */}
        <div className="mt-16 md:mt-20 border-t border-white/10 pt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="text-neutral-500 max-w-md text-sm leading-relaxed">
            Only showing the 5 most-recent reviews. See every review, add your
            own, or check the studio location on Google Maps.
          </p>
          <a
            href={data.url || "https://www.google.com/maps"}
            target="_blank"
            rel="noreferrer"
            data-testid="reviews-google-link"
            className="btn-primary shrink-0"
          >
            Read All on Google <ExternalLink size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
